import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Rocket,
  CheckCircle2,
  Users,
  DollarSign,
  Target,
  TrendingUp,
  ArrowRight,
  Quote,
  Phone,
  Shield,
  Mail,
  Package,
  MapPin
} from 'lucide-react';

const Dashboard = () => {
  const navigate = useNavigate();
  const BLDR_URL = process.env.REACT_APP_BLDR_URL || 'https://bldr.companeze.com';

  const stats = [
    { icon: Users, label: '1000s', sublabel: 'Expert Professionals', color: 'bg-blue-900' },
    { icon: DollarSign, label: '100%', sublabel: 'Paid Solutions', color: 'bg-blue-800' },
    { icon: Target, label: 'Real', sublabel: 'Business Results', color: 'bg-blue-900' },
    { icon: TrendingUp, label: 'No Free', sublabel: 'Gimmicks', color: 'bg-amber-500' },
  ];

  const features = [
    'Individualized business strategy',
    'Simplified wealth creation',
    'Personalized path to success'
  ];

  const pricing = [
    { label: 'AI Interview', price: '$99', desc: 'Discovery session' },
    { label: 'Evaluation Fee', price: '$199', desc: 'One-time to start' },
    { label: 'Monthly Plans', price: 'From $99', desc: '12-month commitment' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Navy Blue */}
      <section className="relative bg-blue-900 text-white overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-blue-700/30 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-800/30 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur rounded-full text-sm font-medium mb-6">
                <Rocket className="w-4 h-4 text-amber-400" />
                World's Fastest Business Development Engine
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold mb-4 leading-tight">
                From <span className="text-amber-400">Idea</span> to{' '}
                <span className="text-amber-400">Income</span>
              </h1>
              
              <p className="text-xl text-blue-200 mb-8">
                Your Individualized Path to Wealth & Business Success
              </p>

              <div className="space-y-3 mb-8">
                {features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-amber-400" />
                    <span className="text-blue-100">{feature}</span>
                  </div>
                ))}
              </div>

              <Button
                size="lg"
                onClick={() => navigate('/interview')}
                className="bg-amber-500 hover:bg-amber-600 text-blue-900 font-semibold text-lg px-8 py-6 rounded-lg shadow-lg"
              >
                Launch Your Business Development <ArrowRight className="ml-2" />
              </Button>
              
              <p className="mt-4 text-sm text-blue-300">
                Get started with Plans From <span className="text-amber-400 font-semibold">$99.00 per month</span> + $199 evaluation fee
              </p>
            </div>

            {/* Logo/Visual */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-amber-500/20 rounded-full blur-3xl scale-150" />
                <div className="relative w-64 h-64 bg-white rounded-3xl flex items-center justify-center shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <span className="text-blue-900 text-8xl font-bold">Cz</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Quote className="w-10 h-10 text-blue-300 mx-auto mb-4" />
          <blockquote className="text-2xl font-medium text-gray-700 italic mb-4">
            "If you can't make money while you're sleeping, you will never truly realize true wealth"
          </blockquote>
          <p className="text-gray-500">— Business Wisdom</p>
        </div>
      </section>

      {/* Pricing Quick View */}
      <section className="py-12 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Investment to Get Started</h2>
          <div className="grid grid-cols-3 gap-4">
            {pricing.map((item, idx) => (
              <Card key={idx} className="border border-gray-200 shadow-lg text-center">
                <CardContent className="p-6">
                  <p className="text-sm text-gray-500 mb-1">{item.label}</p>
                  <p className="text-3xl font-bold text-blue-900">{item.price}</p>
                  <p className="text-xs text-gray-400 mt-1">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, idx) => (
              <Card key={idx} className="border border-gray-200 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className={`w-14 h-14 mx-auto mb-4 rounded-xl ${stat.color} flex items-center justify-center shadow-lg`}>
                    <stat.icon className="w-7 h-7 text-white" />
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stat.label}</p>
                  <p className="text-sm text-gray-500">{stat.sublabel}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Mail & Package Services Section */}
      <section className="py-16 px-6 bg-blue-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Over 2,500 Mail & Package Locations
            </h2>
            <p className="text-xl text-amber-400 font-medium">
              You get your preferred location(s)
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-white/10 border-0 backdrop-blur">
              <CardContent className="p-6 text-center">
                <Mail className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Privacy</h3>
                <p className="text-blue-200">Receive mail at a professional address</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-0 backdrop-blur">
              <CardContent className="p-6 text-center">
                <Shield className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Theft Prevention</h3>
                <p className="text-blue-200">Secure packages from porch pirates</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-0 backdrop-blur">
              <CardContent className="p-6 text-center">
                <Package className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Insured Options</h3>
                <p className="text-blue-200">Protection for valuable shipments</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to Get Started?</h2>
          <p className="text-gray-600 mb-8">Take the first step towards building your business the right way.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/interview')}
              className="bg-blue-900 hover:bg-blue-800 text-white px-8 py-6"
            >
              Start Interview Lite - $99
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => window.open(BLDR_URL, '_blank')}
              className="border-2 border-blue-900 text-blue-900 hover:bg-blue-50 px-8 py-6"
              data-testid="dashboard-go-pro-btn"
            >
              Go For Pro
            </Button>
          </div>
          <p className="mt-6 text-gray-500">
            Or call us: <a href="tel:5104224451" className="text-blue-900 font-medium">(510) 422-4451</a>
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-900 text-blue-200 py-8 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
              <span className="font-bold text-blue-900">Cz</span>
            </div>
            <span className="text-lg font-bold text-white">Companeze</span>
            <span className="text-amber-400 text-sm">BLDR</span>
          </div>
          <p className="text-sm">© 2025 Companeze BLDR. All rights reserved.</p>
          <p className="mt-2 text-sm">
            <a href="tel:5104224451" className="hover:text-white">(510) 422-4451</a>
            {' • '}
            <a href="mailto:info@companeze.com" className="hover:text-white">info@companeze.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Dashboard;
